import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Scale, Banknote, BookOpen, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function fmtAUD(amount) {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(num);
}

function driftColor(drift) {
  const abs = Math.abs(Number(drift) || 0);
  if (abs < 1) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Drift OK' };
  if (abs <= 100) return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Minor Drift' };
  return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Significant Drift' };
}

const BackOfficeFinanceReconciliation = () => {
  const { isLive } = useEnvironment();
  const env = isLive ? 'live' : 'sandbox';
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/backoffice/ledger/summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-Environment': env,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSummary(data);
      } catch (err) {
        toast.error('Failed to load reconciliation data');
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [env]);

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="spinner w-8 h-8" />
          <p className="text-sm text-slate-500">Loading reconciliation data…</p>
        </div>
      </BackOfficeLayout>
    );
  }

  const pinBalance = Number(summary?.pin_aggregate_balance ?? summary?.pin_balance ?? 0);
  const ledgerAUD = Number(summary?.currency_totals?.AUD ?? summary?.totals_by_currency?.AUD ?? 0);
  const drift = Number(summary?.drift ?? (pinBalance - ledgerAUD));
  const dc = driftColor(drift);

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Scale className="w-7 h-7 text-[#0B6445]" />
            Reconciliation
          </h1>
          <p className="text-[#0E1B16]/70 mt-2 mb-6">
            Compare PIN aggregate balance against internal ledger totals to detect drift.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <Banknote className="w-4 h-4" /> PIN Aggregate Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#073B2E]">{fmtAUD(pinBalance)}</p>
            </CardContent>
          </Card>

          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Ledger AUD Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#073B2E]">{fmtAUD(ledgerAUD)}</p>
            </CardContent>
          </Card>

          <Card className={`shadow-sm ${dc.border} ${dc.bg}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${dc.text}`}>
                {Math.abs(drift) < 1 ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                Drift Amount
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <p className={`text-2xl font-bold ${dc.text}`}>{fmtAUD(drift)}</p>
                <Badge variant="outline" className={`${dc.border} ${dc.text} ${dc.bg}`}>
                  {dc.label}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Explanation */}
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-[#073B2E]">How Reconciliation Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[#0E1B16]/70">
            <p>
              <strong>Drift</strong> is calculated as: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">PIN Aggregate Balance − Ledger AUD Total</code>.
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> &lt; $1 — OK
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                <span className="h-2 w-2 rounded-full bg-amber-500" /> $1 – $100 — Minor
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
                <span className="h-2 w-2 rounded-full bg-red-500" /> &gt; $100 — Significant
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeFinanceReconciliation;
